import { Toastify } from "lift-ui";
import apiClient from "@lift-admin/utils/apiClient";
import type {
  AppsFilterRequest,
  AppsListResponse,
  AppRow,
  LiteLLMKeyUsageFilterRequest,
  LiteLLMKeyUsageResponse,
  PromptRow,
  PromptsListResponse,
  SessionRow,
  SessionsFilterRequest,
  SessionsListResponse,
  StatsResponse,
  UserRow,
} from "./types";

// Optional local-dev override: point these calls straight at a locally running
// claude-deploy-mcp, which serves routes without the gateway's
// /claude-deploy-mcp prefix — e.g. VITE_CLAUDE_DEPLOY_MCP_BASE=http://localhost:8000.
// Staging/prod leave it unset and go through the gateway via VITE_BASEURL.
const LOCAL_MCP_BASE = (
  (import.meta.env.VITE_CLAUDE_DEPLOY_MCP_BASE as string | undefined) || ""
)
  .trim()
  .replace(/\/+$/, "");

const BASE = LOCAL_MCP_BASE
  ? `${LOCAL_MCP_BASE}/api/v1/admin`
  : "/claude-deploy-mcp/api/v1/admin";

const buildQuery = (params: Record<string, unknown>): string => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === "all"
    ) {
      return;
    }
    usp.append(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

export async function getStats(): Promise<StatsResponse> {
  try {
    const res = await apiClient.get(`${BASE}/stats`);
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code stats:", error);
    Toastify("error", "Error fetching Claude Code stats");
    throw error;
  }
}

export async function getUsers(): Promise<UserRow[]> {
  try {
    const res = await apiClient.get(`${BASE}/users`);
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code users:", error);
    Toastify("error", "Error fetching users");
    throw error;
  }
}

export async function getSessions(
  filters: SessionsFilterRequest
): Promise<SessionsListResponse> {
  try {
    const res = await apiClient.get(
      `${BASE}/sessions${buildQuery(filters as Record<string, unknown>)}`
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code sessions:", error);
    Toastify("error", "Error fetching sessions");
    throw error;
  }
}

export async function getSessionPrompts(
  sessionId: string,
  userEmail?: string | null
): Promise<PromptRow[]> {
  try {
    const qs = userEmail
      ? `?user_email=${encodeURIComponent(userEmail)}`
      : "";
    const res = await apiClient.get(
      `${BASE}/sessions/${encodeURIComponent(sessionId)}/prompts${qs}`
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching prompts for session:", error);
    Toastify("error", "Error fetching session prompts");
    throw error;
  }
}

export async function getPrompts(
  filters: SessionsFilterRequest & { session_id?: string }
): Promise<PromptsListResponse> {
  try {
    const res = await apiClient.get(
      `${BASE}/prompts${buildQuery(filters as Record<string, unknown>)}`
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code prompts:", error);
    Toastify("error", "Error fetching prompts");
    throw error;
  }
}

export async function getApps(
  filters: AppsFilterRequest
): Promise<AppsListResponse> {
  try {
    const res = await apiClient.get(
      `${BASE}/apps${buildQuery(filters as Record<string, unknown>)}`
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code apps:", error);
    Toastify("error", "Error fetching apps");
    throw error;
  }
}

export async function getApp(appId: number | string): Promise<AppRow> {
  try {
    const res = await apiClient.get(`${BASE}/apps/${appId}`);
    return res.data;
  } catch (error) {
    console.error("Error fetching Claude Code app:", error);
    Toastify("error", "Error fetching app");
    throw error;
  }
}

export async function getLiteLLMKeyUsage(
  filters: LiteLLMKeyUsageFilterRequest
): Promise<LiteLLMKeyUsageResponse> {
  try {
    const res = await apiClient.get(
      `${BASE}/litellm/keys${buildQuery(filters as Record<string, unknown>)}`
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching LiteLLM key usage:", error);
    Toastify("error", "Error fetching key usage");
    throw error;
  }
}

export type { SessionRow };
